import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import Pagination, { PAGE_SIZE } from './Pagination';
import { useToast } from './Toast';
import {
  Inquiry,
  InquiryStatus,
  InquiryStatusCounts,
  listInquiries,
  setInquiryStatus,
} from '../api/inquiries';
import { extractApiError } from '../api/legalServices';
import { Listing, getListing } from '../api/listings';
import ListingDetailInline from './ListingDetailInline';
import { useAuth } from '../context/AuthContext';

type LeadFilter = 'all' | InquiryStatus;
const FILTERS: LeadFilter[] = ['all', 'new', 'contacted', 'converted'];

const statusBadge = (status: InquiryStatus): string => `badge badge-${status}`;

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

interface InquiryListProps {
  /** Only this merchant's inquiries (merchant detail page); all when omitted. */
  agentId?: number;
}

/**
 * Inquiries with search, date and status filters and expandable rows — the
 * Leads & Inquiries page, and the Inquiries list on a merchant's detail page.
 */
const InquiryList: React.FC<InquiryListProps> = ({ agentId }) => {
  const { toast } = useToast();
  const { user } = useAuth();

  /*
   * Actions (Email, Call, Mark Contacted/Converted) are only allowed on an
   * inquiry whose assigned agent is the logged-in user. An admin viewing an
   * inquiry assigned to a different agent — or an unassigned one — can read it
   * but not act on it.
   */
  const canActOn = useCallback(
    (l: Inquiry): boolean =>
      user != null &&
      l.agentId != null &&
      String(l.agentId) === String(user.id),
    [user]
  );

  const [data, setData] = useState<Inquiry[]>([]);
  const [counts, setCounts] = useState<InquiryStatusCounts>({
    all: 0,
    new: 0,
    contacted: 0,
    converted: 0,
  });
  const [filter, setFilter] = useState<LeadFilter>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [listingCache, setListingCache] = useState<Record<number, Listing>>({});
  const [listingBusyId, setListingBusyId] = useState<number | null>(null);
  const [total, setTotal] = useState<number>(0);

  // Debounce search input -> 300ms before hitting the API.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // The page belongs to one set of filters: change a filter and it's page 1
  // again straight away (no extra request for the old page).
  const filterKey = [filter, search, dateFrom, dateTo, agentId].join('|');
  const [paging, setPaging] = useState<{ key: string; page: number }>({ key: filterKey, page: 1 });
  const page = paging.key === filterKey ? paging.page : 1;
  const setPage = useCallback(
    (next: number) => {
      setExpanded(null);
      setPaging({ key: filterKey, page: next });
    },
    [filterKey]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listInquiries({
        status: filter === 'all' ? undefined : filter,
        agentId,
        search: search || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        limit: PAGE_SIZE,
      });
      // The last page emptied (e.g. a status change moved its only row).
      if (res.inquiries.length === 0 && page > 1 && res.totalPages < page) {
        setPage(Math.max(1, res.totalPages));
        return;
      }
      setData(res.inquiries);
      setCounts(res.counts);
      setTotal(res.total);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load inquiries'));
    } finally {
      setLoading(false);
    }
  }, [filter, search, dateFrom, dateTo, agentId, page, setPage]);

  const hasFilters = Boolean(searchInput || dateFrom || dateTo);
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
  };

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => data, [data]);

  const applyStatus = async (l: Inquiry, next: InquiryStatus) => {
    // Guard: only the assigned agent may change status. The buttons are already
    // hidden otherwise; this stops any request slipping through.
    if (!canActOn(l)) return;
    setBusyId(l.id);
    // Optimistic update.
    const prevStatus = l.status;
    setData((prev) =>
      prev.map((x) => (x.id === l.id ? { ...x, status: next } : x))
    );
    try {
      const updated = await setInquiryStatus(l.id, next);
      setData((prev) =>
        prev.map((x) => (x.id === l.id ? { ...x, ...updated } : x))
      );
      // Refresh counts so the tab pills stay accurate.
      load();
      toast(
        `${l.customerName} marked ${next}`,
        next === 'converted' ? 'success' : 'info'
      );
    } catch (err) {
      setData((prev) =>
        prev.map((x) => (x.id === l.id ? { ...x, status: prevStatus } : x))
      );
      toast(extractApiError(err, 'Failed to update inquiry'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const toggleRow = (l: Inquiry) => {
    const next = expanded === l.id ? null : l.id;
    setExpanded(next);
    if (next !== null && l.listingId && !listingCache[l.listingId]) {
      setListingBusyId(l.id);
      getListing(l.listingId)
        .then((listing) =>
          setListingCache((prev) => ({ ...prev, [listing.id]: listing }))
        )
        .catch((err) =>
          toast(extractApiError(err, 'Failed to load listing'), 'error')
        )
        .finally(() => setListingBusyId(null));
    }
  };

  const headerSubtitle =
    counts.all === 1
      ? '1 customer inquiry'
      : `${counts.all} customer inquiries`;

  return (
    <div>
      <div className="text-sm text-g500 mb-3">{headerSubtitle}</div>

      {/* Filters */}
      <div className="card p-4 mb-4 flex items-end flex-wrap gap-3">
        <div className="flex-1 min-w-[240px]">
          <label className="form-label">Search</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2">
              <Icon name="search" size={18} color="var(--g400)" />
            </span>
            <input
              className="form-input"
              style={{ paddingLeft: 40 }}
              placeholder="Customer, merchant or property name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
        <div className="w-[170px]">
          <label className="form-label">From</label>
          <input
            type="date"
            className="form-input"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="w-[170px]">
          <label className="form-label">To</label>
          <input
            type="date"
            className="form-input"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={clearFilters}
          disabled={!hasFilters}
          style={hasFilters ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
        >
          <Icon name="x" size={14} /> Clear
        </button>
      </div>

      <div className="flex justify-end items-center flex-wrap gap-3 mb-5">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => {
            const active = filter === f;
            const n = counts[f];
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="capitalize"
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  border: `1px solid ${
                    active ? 'var(--brand)' : 'var(--g200)'
                  }`,
                  background: active ? 'var(--brand-light)' : '#fff',
                  color: active ? 'var(--brand)' : 'var(--g600)',
                  transition: 'all .15s ease',
                }}
              >
                {f}{typeof n === 'number' ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>
      </div>

      {loadError && (
        <div
          style={{
            background: '#fff5f5',
            border: '1px solid #fed7d7',
            color: '#c53030',
            padding: '10px 14px',
            borderRadius: 10,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {loadError}
        </div>
      )}

      <div
        className="bg-white rounded-card overflow-hidden border border-g100"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {['Customer', 'Property', 'Merchant Assigned', 'Date', 'Status', 'Details'].map((h, i) => (
                <th
                  key={h}
                  className={
                    'px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap' +
                    (i === 5 ? ' text-right' : '')
                  }
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-g500">
                  Loading inquiries…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-g500">
                  {hasFilters || filter !== 'all'
                    ? 'No inquiries match these filters.'
                    : 'No inquiries found.'}
                </td>
              </tr>
            )}
            {!loading && filtered.map((l) => {
              const isOpen = expanded === l.id;
              const propertyLabel = l.listing?.address || l.listing?.title || '—';
              const agentLabel = l.agent?.name || l.agent?.email || '—';
              return (
                <React.Fragment key={l.id}>
                  <tr
                    className="cursor-pointer transition-colors"
                    style={{ background: isOpen ? 'var(--g50)' : undefined }}
                    onClick={() => toggleRow(l)}
                  >
                    <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                      {l.customerName}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span className="flex items-center gap-2">
                        <Icon name="map-pin" size={14} color="var(--g400)" />
                        {propertyLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      {agentLabel}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-g500 whitespace-nowrap border-b border-g100">
                      {formatDate(l.createdAt)}
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                      <span className={statusBadge(l.status)}>
                        {l.status[0].toUpperCase() + l.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100 text-right">
                      {/* The whole row toggles too; this makes it obvious, and
                          reachable by keyboard. */}
                      <button
                        type="button"
                        className="inline-flex w-7 h-7 rounded-full items-center justify-center hover:opacity-80"
                        style={{
                          background: isOpen ? 'var(--brand)' : 'var(--g100)',
                          border: `1px solid ${isOpen ? 'var(--brand)' : 'var(--g200)'}`,
                          transition: 'transform .2s, background .2s',
                          transform: `rotate(${isOpen ? 180 : 0}deg)`,
                        }}
                        aria-label={isOpen ? 'Hide details' : 'Show details'}
                        aria-expanded={isOpen}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(l);
                        }}
                      >
                        <Icon
                          name="chevron-down"
                          size={18}
                          strokeWidth={2.5}
                          color={isOpen ? '#fff' : 'var(--g700)'}
                        />
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <div
                          className="animate-slideIn"
                          style={{
                            background: 'var(--g50)',
                            borderBottom: '1px solid var(--g200)',
                          }}
                        >
                        <div className="flex gap-8 flex-wrap px-6 pt-5 pb-4">
                          <div>
                            <div className="text-[13px] font-bold text-g500 uppercase tracking-wider mb-2.5">
                              Contact
                            </div>
                            <div className="flex items-center gap-2 text-sm mb-1.5">
                              <Icon name="mail" size={14} color="var(--g400)" />
                              {l.email}
                            </div>
                            {l.phone && (
                              <div className="flex items-center gap-2 text-sm">
                                <Icon name="phone" size={14} color="var(--g400)" />
                                {l.phone}
                              </div>
                            )}
                            {l.user && (
                              <div className="text-[12px] text-g500 mt-2">
                                Submitted by user #{l.user.id} ({l.user.email})
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <div className="text-[13px] font-bold text-g500 uppercase tracking-wider mb-2.5">
                              Notes
                            </div>
                            <div
                              className="text-sm text-g700 leading-relaxed bg-white px-4 py-3 rounded-[10px] border border-g200"
                            >
                              {l.message || '(no message)'}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {canActOn(l) ? (
                              <>
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `mailto:${l.email}`;
                                  }}
                                >
                                  <Icon name="mail" size={14} color="#fff" /> Email
                                </button>
                                {l.phone && (
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.location.href = `tel:${l.phone}`;
                                    }}
                                  >
                                    <Icon name="phone" size={14} /> Call
                                  </button>
                                )}
                                {l.status === 'new' && (
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    disabled={busyId === l.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      applyStatus(l, 'contacted');
                                    }}
                                  >
                                    Mark Contacted
                                  </button>
                                )}
                                {l.status === 'contacted' && (
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    disabled={busyId === l.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      applyStatus(l, 'converted');
                                    }}
                                  >
                                    Mark Converted
                                  </button>
                                )}
                              </>
                            ) : (
                              <div
                                className="text-[12px] text-g500 flex items-center gap-1.5 text-right"
                                style={{ maxWidth: 200 }}
                              >
                                <Icon name="lock" size={13} color="var(--g400)" />
                                <span>
                                  Assigned to {l.agent?.name || l.agent?.email || 'another merchant'} —
                                  only the assigned merchant can act on this inquiry.
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {l.listingId && (
                          <div className="px-6 pb-5">
                            <div className="text-[13px] font-bold text-g500 uppercase tracking-wider mb-2.5">
                              Property Details
                            </div>
                            {listingBusyId === l.id && !listingCache[l.listingId] ? (
                              <div className="text-sm text-g500 py-2">
                                Loading listing details…
                              </div>
                            ) : listingCache[l.listingId] ? (
                              <ListingDetailInline listing={listingCache[l.listingId]} />
                            ) : (
                              <div className="text-sm text-g400 py-2">
                                Listing details unavailable.
                              </div>
                            )}
                          </div>
                        )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} disabled={loading} />
    </div>
  );
};

export default InquiryList;
